import type {
  AttentionOrigin,
  BillingStatus,
  ConnectivityType,
  CurrentUser,
  DocumentSource,
  DocumentType,
  DynamicFieldType,
  ExternalHandoffStatus,
  ExternalSystemType,
  LiveSessionStatus,
  PatientDataStatus,
  PatientStatus,
  ProcessStateCode,
  RoleCode,
} from '@consultorio/contracts';

export type {
  AttentionOrigin,
  BillingStatus,
  ConnectivityType,
  CurrentUser,
  DocumentSource,
  DocumentType,
  DynamicFieldType,
  ExternalHandoffStatus,
  ExternalSystemType,
  LiveSessionStatus,
  PatientDataStatus,
  PatientStatus,
  ProcessStateCode,
  RoleCode,
};

export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface PatientQuickResult {
  id: number;
  code: string | null;
  fullName: string;
  identificationType: string;
  identificationNumber: string;
  normalizedDocument: string;
  primaryPhone: string | null;
  email: string | null;
  dataStatus: PatientDataStatus;
  createdFromOrigin: AttentionOrigin;
}

export interface PatientSummary extends PatientQuickResult {
  birthDate: string | null;
  city: string | null;
  status: PatientStatus;
  rowVersion: number;
  updatedAt: string;
  _count: { studies: number };
}

export interface PatientDetail extends PatientSummary {
  firstName: string;
  secondName: string | null;
  firstLastName: string;
  secondLastName: string | null;
  sex: string | null;
  address: string | null;
  department: string | null;
  authorizesData: boolean;
  authorizationDate: string | null;
  dataObservation: string | null;
  dataValidationNotes: string | null;
  validatedByEmail: string | null;
  validatedAt: string | null;
  studies: Array<{
    id: number;
    code: string | null;
    studyDate: string;
    studyTypeName: string;
    stateCode: string;
    stateName: string;
    origin: AttentionOrigin;
    doctorEmail: string;
    billingHandoff: { status: BillingStatus } | null;
  }>;
  consentEvents: Array<{
    id: number;
    authorized: boolean;
    observation: string | null;
    occurredAt: string;
  }>;
}

export interface StudyTypeField {
  id: number;
  studyTypeId: number;
  code: string;
  label: string;
  fieldType: DynamicFieldType;
  required: boolean;
  active: boolean;
  displayOrder: number;
  helpText: string | null;
  options: string[] | null;
  appliesToRole: RoleCode | null;
}

export interface StudyType {
  id: number;
  code: string;
  name: string;
  active: boolean;
  requiresWeight: boolean;
  requiresHeight: boolean;
  minimumAttachments: number;
  maximumAttachments: number | null;
  requiresRecommendations: boolean;
  expectedDocumentTypes: DocumentType[];
  reportTemplateKey: string | null;
  displayOrder: number;
  fields: StudyTypeField[];
}

export interface Institution {
  id: number;
  code: string;
  name: string;
  type: string;
  email: string | null;
  city: string | null;
  active: boolean;
  locations?: ServiceLocation[];
}

export interface ServiceLocation {
  id: number;
  institutionId: number | null;
  code: string;
  name: string;
  address: string | null;
  city: string | null;
  contactName: string | null;
  contactPhone: string | null;
  active: boolean;
  institution?: { id: number; code: string; name: string } | null;
}

export interface ThirdParty {
  id: number;
  sourceSystem: string;
  externalId: string;
  name: string;
  identificationType: string;
  identificationNumber: string;
  personType: 'JURIDICA';
  active: boolean;
}

export interface Agreement {
  id: number;
  sourceSystem: string;
  externalId: string;
  name: string;
  thirdPartyId: number;
  active: boolean;
  thirdParty: Pick<
    ThirdParty,
    'id' | 'externalId' | 'name' | 'identificationNumber' | 'active'
  >;
}

export interface Professional {
  id: number;
  code: string;
  fullName: string;
  email: string;
  type: string;
  registrationNumber: string | null;
  signatureFileUrl: string | null;
  active: boolean;
}

export interface ProcessState {
  id: number;
  code: string;
  name: string;
  processOrder: number;
  responsibleRole: string;
  slaMinutes: number | null;
  isFinal: boolean;
}

export interface Transition {
  id: number;
  currentStateCode: string;
  actionCode: string;
  actionLabel: string;
  targetStateCode: string;
  authorizedRoles: RoleCode[];
  requiresComment: boolean;
  applicableOrigin: string;
}

export interface SystemParameter {
  id: number;
  key: string;
  textValue: string | null;
  numberValue: number | null;
  booleanValue: boolean | null;
  urlValue: string | null;
  description: string;
  active: boolean;
  sensitive: boolean;
}

export interface Catalogs {
  studyTypes: StudyType[];
  institutions: Institution[];
  serviceLocations: ServiceLocation[];
  professionals: Professional[];
  thirdParties: ThirdParty[];
  agreements: Agreement[];
  states: ProcessState[];
  transitions: Transition[];
  parameters: SystemParameter[];
}

export interface ClinicalReportTemplate {
  id: number;
  name: string;
  code: string;
  description: string | null;
  category: string | null;
  studyTypeId: number | null;
  contentJson: { type: 'doc'; content: unknown[] };
  contentText: string;
  contentKind: 'PLANTILLA_COMPLETA' | 'BLOQUE_CLINICO';
  status: 'BORRADOR' | 'PUBLICADA' | 'INACTIVA';
  version: number;
  lineageCode: string;
  publishedAt: string | null;
  active: boolean;
  systemTemplate: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  studyType?: Pick<StudyType, 'id' | 'code' | 'name'> | null;
  createdBy?: { email: string; displayName: string } | null;
  updatedBy?: { email: string; displayName: string } | null;
}

export interface ClinicalTemplatePreference {
  id: number;
  userId: number;
  studyTypeId: number;
  templateId: number;
  template: ClinicalReportTemplate;
  createdAt: string;
  updatedAt: string;
}

export interface Cie10SearchResult {
  code: string;
  description: string;
  sex: string | null;
  minimumAge: number | null;
  maximumAge: number | null;
  validity: number;
}

export interface StudySummary {
  id: number;
  code: string | null;
  patientId: number;
  patientCode: string;
  patientName: string;
  normalizedDocument: string;
  origin: AttentionOrigin;
  institutionName: string | null;
  serviceLocation: string | null;
  studyDate: string;
  studyTypeName: string;
  doctorEmail: string;
  technicianEmail: string | null;
  procedureStatus: string;
  stateCode: ProcessStateCode | string;
  stateName: string;
  currentResponsibleRole: string;
  currentResponsibleEmail: string | null;
  stateEnteredAt: string;
  slaStatus: 'DENTRO_SLA' | 'FUERA_SLA' | 'SIN_SLA_ACTIVO' | 'ERROR_OPERATIVO';
  slaElapsedMinutes: number | null;
  slaLimitMinutes: number | null;
  generationStatus: string;
  deliveryStatus: string;
  requiresLiveSupport: boolean;
  sourceFilesComplete: boolean;
  rowVersion: number;
  updatedAt: string;
  patient: { dataStatus: PatientDataStatus };
  billingHandoff: { status: BillingStatus } | null;
  _count: { documents: number; liveSessions: number };
}

export interface DocumentRecord {
  id: number;
  studyId: number;
  type: DocumentType;
  source: DocumentSource;
  fileName: string;
  mimeType: string;
  sizeBytes: string | number;
  status: string;
  storageProvider: string;
  storageKey: string;
  sharePointSiteId: string | null;
  driveId: string | null;
  driveItemId: string | null;
  reportVersion: number | null;
  templateVersion: string | null;
  order: number;
  includeInReport: boolean;
  qualityValidated: boolean;
  retentionUntil: string | null;
  createdAt: string;
  uploadedByEmail: string | null;
}

export interface ReportRecord {
  id: number;
  version: number;
  status: string;
  textReport: string;
  findings: string | null;
  conclusionDiagnosis: string;
  recommendations: string | null;
  chiefComplaint: string | null;
  currentIllness: string | null;
  physicalExam: string | null;
  clinicalImpression: string | null;
  managementPlan: string | null;
  careModality: string | null;
  templateVersion: string | null;
  richContentJson: { type: 'doc'; content: unknown[] } | null;
  richContentText: string | null;
  selectedTemplateId: number | null;
  diagnoses: Array<{
    id: number;
    isPrimary: boolean;
    cie10Code: string;
    description: string;
    diagnosisType: string | null;
    classification: string | null;
    registeredAt: string | null;
    displayOrder: number;
  }>;
  internalObservations: string | null;
  medicalDecision: string | null;
  returnComment: string | null;
  professionalId: number;
  professionalName: string;
  professionalRegistration: string | null;
  lockedAt: string | null;
  lockedByEmail: string | null;
  approvedAt: string | null;
  approvedByEmail: string | null;
  isCurrent: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LiveSessionRecord {
  id: number;
  status: LiveSessionStatus;
  startedAt: string | null;
  endedAt: string | null;
  technicalUserEmail: string;
  doctorUserEmail: string;
  connectivityType: ConnectivityType;
  meetingReference: string | null;
  incidentDescription: string | null;
  result: string | null;
  recordingUrl: string | null;
  recordingAuthorized: boolean;
  createdAt: string;
}

export interface BillingHandoffRecord {
  id: number;
  status: BillingStatus;
  recipientEmail: string | null;
  invoiceNumber: string | null;
  payerName: string | null;
  amount: number | string | null;
  pendingInformation: string | null;
  returnComment: string | null;
  sentAt: string | null;
  receivedAt: string | null;
  billedAt: string | null;
  updatedAt: string;
}

export interface ExternalHandoffRecord {
  id: number;
  systemType: ExternalSystemType;
  status: ExternalHandoffStatus;
  externalReference: string | null;
  notes: string | null;
  metadata: Record<string, unknown> | null;
  completedAt: string | null;
  updatedAt: string;
}

export interface StudyDetail extends StudySummary {
  reason: string;
  nonPerformanceReason: string | null;
  startedAt: string | null;
  endedAt: string | null;
  referringProfessional: string | null;
  referringInstitution: string | null;
  profusionPatientCode: string | null;
  profusionStudyCode: string | null;
  receptionEmail: string | null;
  administrativeCondition: string | null;
  payerName: string | null;
  agreementId: number | null;
  agreementExternalId: string | null;
  agreementName: string | null;
  thirdPartyName: string | null;
  authorizationNumber: string | null;
  administrativeObservation: string | null;
  connectivityType: ConnectivityType;
  scheduledAt: string | null;
  sendToPatient: boolean;
  sendToAilyn: boolean;
  additionalRecipients: string[];
  finalPdfUrl: string | null;
  errorDetail: string | null;
  patient: PatientDetail;
  studyType: StudyType;
  institution: Institution | null;
  serviceLocationRef: ServiceLocation | null;
  doctor: Professional;
  technicalCapture: Record<string, unknown> | null;
  liveSessions: LiveSessionRecord[];
  billingHandoff: BillingHandoffRecord | null;
  externalHandoffs: ExternalHandoffRecord[];
  reports: ReportRecord[];
  history: Array<{
    id: number;
    actionCode: string;
    previousStateCode: string | null;
    newStateCode: string;
    executedByEmail: string;
    comment: string | null;
    transitionedAt: string;
  }>;
  documents: DocumentRecord[];
  emailDeliveries: Array<Record<string, unknown>>;
  automationRuns: Array<Record<string, unknown>>;
  dynamicValues: Record<string, unknown>;
}

export interface DashboardData {
  metrics: {
    myPending: number;
    technicalPending: number;
    overdue: number;
    errors: number;
    sentToday: number;
    pendingValidation: number;
    pendingBilling: number;
    liveSessionsToday: number;
  };
  recentStudies: Array<
    StudySummary & {
      patient: { dataStatus: PatientDataStatus };
      billingHandoff: { status: BillingStatus } | null;
    }
  >;
  byState: Array<{
    stateCode: string;
    stateName: string;
    _count: { _all: number };
  }>;
  byOrigin: Array<{ origin: AttentionOrigin; _count: { _all: number } }>;
  overdueByState: Array<{
    stateCode: string;
    stateName: string;
    responsibleRole: RoleCode;
    count: number;
  }>;
}

export interface IntegrationReadiness {
  auth: { driver: string; ready: boolean };
  storage: { driver: string; ready: boolean };
  mail: { driver: string; ready: boolean; delegated: boolean };
  liveSession: { driver: string; ready: boolean };
  pending: {
    billingEmail: boolean;
    reportTemplate: boolean;
    doctorSignature: boolean;
  };
}

export interface AdminUser {
  id: number;
  email: string;
  displayName: string;
  role: RoleCode;
  active: boolean;
  professionalId: number | null;
  entraTenantId: string | null;
  entraObjectId: string | null;
  roleAssignments: Array<{ id: number; role: RoleCode; active: boolean }>;
  professional?: Professional | null;
}

export function hasAnyRole(
  user: CurrentUser | null | undefined,
  roles: RoleCode[],
): boolean {
  if (!user) return false;
  return user.roles.some((role) => roles.includes(role));
}
